import { useEffect, useState } from "react";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { PushPin, Heart, Megaphone, NotePencil, Trash, PaperPlaneRight } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";

const KINDS = [
  { key: "note", label: "Note", icon: NotePencil },
  { key: "tribute", label: "Tribute", icon: Heart },
  { key: "shoutout", label: "Shout-out", icon: Megaphone },
];
const KIND_STYLE = {
  note: "bg-accent text-primary",
  tribute: "bg-secondary/15 text-secondary",
  shoutout: "bg-primary/10 text-primary",
};

export default function StoryBoard({ boardType, boardId, canPost = true, title = "Storyboard" }) {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [text, setText] = useState("");
  const [kind, setKind] = useState("note");
  const [posting, setPosting] = useState(false);

  const load = () => api.get("/storyboards", { params: { board_type: boardType, board_id: boardId } }).then((r) => setPosts(r.data)).catch(() => {});
  useEffect(() => { if (boardId) load(); }, [boardType, boardId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setPosting(true);
    try {
      await api.post("/storyboards", { board_type: boardType, board_id: boardId, kind, text });
      setText(""); setKind("note");
      toast.success("Posted to the storyboard");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not post"); }
    finally { setPosting(false); }
  };

  const remove = async (id) => { await api.delete(`/storyboards/${id}`); load(); };

  return (
    <div data-testid="storyboard">
      <div className="mb-4 flex items-center gap-2">
        <PushPin className="text-secondary" size={20} weight="duotone" />
        <h2 className="font-heading text-xl font-medium">{title}</h2>
      </div>

      {canPost && (
        <form onSubmit={submit} className="mb-6 rounded-xl border border-border/60 bg-card p-4">
          <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Leave a note, tribute or shout-out…" className="resize-none border-0 p-0 shadow-none focus-visible:ring-0" data-testid="storyboard-input" />
          <div className="mt-3 flex items-center justify-between border-t border-border/60 pt-3">
            <div className="flex gap-1.5">
              {KINDS.map((k) => (
                <button key={k.key} type="button" onClick={() => setKind(k.key)}
                  className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs transition-colors ${kind === k.key ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-accent"}`}
                  data-testid={`storyboard-kind-${k.key}`}>
                  <k.icon size={13} /> {k.label}
                </button>
              ))}
            </div>
            <Button type="submit" size="sm" className="rounded-full gap-1.5" disabled={posting} data-testid="storyboard-post"><PaperPlaneRight size={14} weight="fill" /> Post</Button>
          </div>
        </form>
      )}

      <div className="space-y-4">
        {posts.length === 0 && <p className="text-sm text-muted-foreground">No posts yet. Be the first to leave a memory.</p>}
        {posts.map((p, i) => {
          const initials = (p.author_name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("");
          const K = KINDS.find((k) => k.key === p.kind) || KINDS[0];
          return (
            <div key={p.id} className="animate-fade-up rounded-xl border border-border/60 bg-card p-4" style={{ animationDelay: `${i * 0.03}s` }} data-testid="storyboard-post">
              <div className="flex items-start gap-3">
                <Avatar className="h-9 w-9"><AvatarImage src={fileUrl(p.avatar_file_id)} /><AvatarFallback className="bg-accent text-xs">{initials}</AvatarFallback></Avatar>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{p.author_name}</span>
                    <Badge className={`rounded-full border-0 font-normal ${KIND_STYLE[p.kind]}`}><K.icon size={11} className="mr-1" /> {K.label}</Badge>
                    <span className="text-xs text-muted-foreground">{new Date(p.created_at).toLocaleDateString()}</span>
                  </div>
                  <p className="mt-1.5 leading-relaxed text-foreground/90">{p.text}</p>
                </div>
                {p.author_id === user?.id && (
                  <button onClick={() => remove(p.id)} className="text-muted-foreground hover:text-destructive" data-testid="storyboard-delete"><Trash size={16} /></button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
